import { useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Card } from '@/components/common';
import { cn } from '@/utils';
import { MICROSITE_DEVICES, type MicrositeDevice } from '@/services/microsite';

const DEVICE_ICONS: Record<MicrositeDevice, React.ElementType> = {
  desktop: ComputerDesktopIcon,
  tablet: DeviceTabletIcon,
  mobile: DevicePhoneMobileIcon,
};

/** Viewport heights to render at, per device. The frame scrolls internally. */
const FRAME_HEIGHTS: Record<MicrositeDevice, number> = {
  desktop: 820,
  tablet: 940,
  mobile: 760,
};

export function DeviceSwitcher({
  device,
  onChange,
  className,
}: {
  device: MicrositeDevice;
  onChange: (device: MicrositeDevice) => void;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label="Preview viewport"
      className={cn(
        'inline-flex items-center gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-700',
        className,
      )}
    >
      {MICROSITE_DEVICES.map(({ value, label, width }) => {
        const Icon = DEVICE_ICONS[value];
        const active = device === value;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            title={`${label} — ${width}px`}
            onClick={() => onChange(value)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
              active
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-white'
                : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200',
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The rendered page, scaled to fit whatever width the pane has.
 *
 * The frame is always rendered at the device's real CSS width and then scaled
 * down with a transform. Setting the iframe to the pane's width instead would
 * change which media queries match, so the "mobile" preview would only look
 * like a phone by coincidence of the pane being narrow.
 */
function PreviewFrame({ html, device }: { html: string; device: MicrositeDevice }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const width = MICROSITE_DEVICES.find((d) => d.value === device)?.width ?? 1280;
  const height = FRAME_HEIGHTS[device];

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const fit = () => setScale(Math.min(1, (el.clientWidth || width) / width));
    fit();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', fit);
      return () => window.removeEventListener('resize', fit);
    }
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [width]);

  return (
    <div ref={containerRef} className="overflow-hidden">
      <div style={{ height: height * scale }}>
        <iframe
          title={`${device} preview`}
          srcDoc={html}
          /* No allow-scripts and no allow-same-origin: the preview is static
             HTML, so the frame needs neither, and withholding both means a
             section config can never reach the admin's session. allow-popups
             keeps the `<base target="_blank">` links working. */
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          style={{
            width,
            height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            border: 0,
            background: '#fff',
            display: 'block',
          }}
        />
      </div>
    </div>
  );
}

export function PreviewPane({
  html,
  device,
  onDeviceChange,
  isLoading,
  isFetching,
  error,
  onRetry,
  sticky = true,
}: {
  html: string | undefined;
  device: MicrositeDevice;
  onDeviceChange: (device: MicrositeDevice) => void;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  onRetry?: () => void;
  sticky?: boolean;
}) {
  return (
    <Card className={cn('overflow-hidden p-0', sticky && 'lg:sticky lg:top-4')}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Live preview</span>
          {isFetching && (
            <ArrowPathIcon
              className="h-4 w-4 animate-spin text-gray-400"
              aria-label="Refreshing preview"
            />
          )}
        </div>
        <DeviceSwitcher device={device} onChange={onDeviceChange} />
      </div>

      {error ? (
        <div className="flex flex-col items-center gap-3 p-8 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
          <p className="max-w-sm text-sm text-gray-600 dark:text-gray-300">{error}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              Try again
            </button>
          )}
        </div>
      ) : isLoading && !html ? (
        <div className="flex h-64 items-center justify-center text-sm text-gray-400">
          Rendering preview…
        </div>
      ) : html ? (
        <PreviewFrame html={html} device={device} />
      ) : (
        <div className="flex h-64 items-center justify-center px-6 text-center text-sm text-gray-400">
          Add a section to see your page here.
        </div>
      )}

      <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-400 dark:border-gray-700 dark:text-gray-500">
        Rendered by the same templates as your live site, including unsaved edits. Sections hidden
        on this breakpoint are left out entirely, exactly as a real visitor would get them.
      </p>
    </Card>
  );
}
